<script lang="ts">
  import { fade } from 'svelte/transition';

  import SectionTitle from '$components/section-title.svelte';
  import { button } from '$components/ui/button.svelte';

  import { t } from '$lib/i18n';
  import { cn } from '$lib/utils';
  import { reducedMotion } from '$lib/stores/reduced-motion';
  import { storyblokEditable } from '$lib/actions/storyblok-editable';
  import { intersectionObserver } from '$lib/actions/intersection-observer';
  import { getAnchorFromCmsLink, getImageAttributes } from '$lib/utils/cms';

  import type { HeroStoryblok } from '$types/bloks';
  import Image from '$components/image.svelte';
  import { buildSizes, buildSrcSet } from '$lib/image';
  import { onMount } from 'svelte';
  import { zoomOnScrollHandler } from '$lib/transitions';

  export let block: HeroStoryblok;

  onMount(() => {
    if (block.slight_zoom_on_scroll && !$reducedMotion) {
      const handler = () => {
        zoomOnScrollHandler(imageContainer, (n) => (scale = n));
      };
      window.addEventListener('scroll', handler);
      return () => window.removeEventListener('scroll', handler);
    }
  });

  $: shouldParallax = block.video?.filename || block.image?.filename;

  let scroll: number;
  let savedScroll = 0;
  let animate = true;

  let video: HTMLVideoElement;
  let isVideoPlaying = false;

  let imageContainer: HTMLDivElement;
  let scale = 1;
</script>

<svelte:window bind:scrollY={scroll} />

<section
  class={cn(
    'grid overflow-hidden px-2 pb-12 pt-32 lg:px-8 lg:pb-16',
    !block.video?.filename && block.image?.filename ? 'min-h-[100vh]' : ''
  )}
  style:--scale={scale}
>
  <div
    class="container mx-auto flex h-full flex-col"
    use:storyblokEditable={block}
    use:intersectionObserver={{
      callback: ([e]) => {
        if (e.isIntersecting) {
          animate = true;
        } else {
          animate = false;
        }
      },
      options: { threshold: [0] }
    }}
  >
    <div
      style:transform={shouldParallax
        ? `translate3d(0,${animate ? scroll * 0.5 : savedScroll * 0.5}px,0)`
        : undefined}
    >
      <SectionTitle as="h1" block={block.title[0]} />
    </div>

    {#if block.image?.filename}
      {#if block.video?.filename}
        {@const { src: poster } = getImageAttributes(block.image, { size: [1440, 0] })}
        <div
          class="relative z-10 overflow-hidden rounded-md isolate flex grow items-end"
          bind:this={imageContainer}
        >
          <button
            aria-label={isVideoPlaying ? 'Pause video' : 'Play video'}
            on:click={() => (isVideoPlaying ? video?.pause() : video?.play())}
            class="w-full after:absolute after:top-full after:pointer-events-none after:bg-background after:left-0 after:right-0 after:h-full"
          >
            <!-- svelte-ignore a11y-media-has-caption -->
            <video
              {poster}
              bind:this={video}
              on:pause={() => (isVideoPlaying = false)}
              on:play={() => (isVideoPlaying = true)}
              on:ended={() => video.load()}
              src={block.video?.filename}
              class="w-full rounded-md block scale-[var(--scale)]"
            />

            {#if !video || !isVideoPlaying}
              <div
                class={cn(
                  button({ variant: 'secondary', size: 'md' }),
                  'absolute z-10 bottom-4 left-4 backdrop-blur right-4 lg:right-[unset]'
                )}
                transition:fade={{ duration: $reducedMotion ? 0 : 150 }}
              >
                {$t('hero.watch-video')}
              </div>
            {/if}
          </button>
        </div>
      {:else if !!block.cta?.length}
        {@const { href } = $getAnchorFromCmsLink(block.cta[0].link)}
        <div
          class="relative overflow-hidden rounded-md z-10 flex min-h-[560px] grow items-end p-4"
          bind:this={imageContainer}
        >
          <a {href}>
            <picture>
              {#if block.mobile_image?.filename}
                <source
                  media="(max-width: 768px)"
                  sizes="100vw"
                  srcset={buildSrcSet({
                    loader: ({ width }) => {
                      return block.mobile_image
                        ? getImageAttributes(block.mobile_image, { size: [width, 0] }).src
                        : '';
                    },
                    widths: [(w) => w]
                  })}
                />
              {/if}
              <source
                sizes={buildSizes({
                  default: '100vw',
                  lg: '1440px'
                })}
                srcset={buildSrcSet({
                  loader: ({ width }) => {
                    return block.image
                      ? getImageAttributes(block.image, { size: [width, 0] }).src
                      : '';
                  },
                  widths: [1440, (w) => (w < 1440 ? w : null)]
                })}
              />
              <Image
                loading="eager"
                class="absolute inset-0 -z-10 h-full w-full scale-[var(--scale)] transform-gpu rounded-md object-cover overflow-hidden"
                {...getImageAttributes(block.image, { size: [1440, 0] })}
              />
            </picture>
            <div
              class={cn(
                button({ variant: 'secondary', size: 'md' }),
                'w-full backdrop-blur lg:w-fit'
              )}
            >
              {block.cta[0]?.label}
            </div>
          </a>
        </div>
      {/if}
    {/if}
  </div>
</section>
